import { useState, useEffect, useRef } from "react";
import {
  Box,
  Modal,
  Fade,
  Backdrop,
  Typography,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { ExpandMore } from "@material-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import { getCategories, setPreference, setCategoryPreferences } from "../../actions";
import Categories from "../categories/categories";
import { resolveSeedExamType } from "../../utils/qlink-exam-seed";
import { isNativeCapacitorRuntime } from "../../services/revenueCatService";
import "./exam-preferences-modal.scss";

// The privacy policy the agreement text incorporates (its "Privacy Policy"
// link), named in the consent line.
export const AGREEMENT_PRIVACY_POLICY_URL = "https://www.medlearnity.com/privacy-policy";

// Why the seeded exam is selected, in the student's terms. "link": a
// ?practice=absite link or an ABSITE campaign (a first touch can be weeks old,
// so it names no link); otherwise the question the visitor just answered
// (QLink, Daily).
const seedNote = (exam, source) => (source === "link"
  ? `${exam} is selected for you. Change it below if you're preparing for a different exam.`
  : `We selected ${exam} from the question you just answered. Continue, or change it below.`);

/*
 * The new-account step on /create-quiz (with `agreementText`), or the exam
 * picker alone (without it, for an account that has already accepted).
 *
 * With `agreementText` this is ONE screen: the exam choice, the agreement one
 * tap away, and the owner's clickwrap (2026-09-29): an UNTICKED "I agree to
 * ..." box, no scroll-to-bottom gate, Continue only once it is ticked. It
 * replaces the agreement modal that used to come first (an iPhone sign-up
 * stopped there, 2026-10-28 funnel study). Saving is the same as the picker's
 * alone, so the backend sees no difference: the exam choice, then
 * userAcknowledged. `onSaveStart` runs when a save begins.
 */
export default function ExamPreferencesModal({
  open, onComplete, mandatory = true, seedExamType, seedSource, agreementText, onSaveStart,
}) {
  const withAgreement = !!agreementText;
  const [agreed, setAgreed] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  // Bumped by the consent line's "Terms of Use" link: open the text, then
  // bring it to its start and focus it once it is on screen.
  const [termsFocusRequest, setTermsFocusRequest] = useState(0);
  const termsRef = useRef(null);
  const [selectedExamType, setSelectedExamType] = useState([]);
  // The exam preselected from the QLink question the visitor came from, if
  // any. Applied once: the visitor can untick it or pick others.
  const [seededExamType, setSeededExamType] = useState("");
  const seedAppliedRef = useRef(false);
  const [examTypeList, setExamTypeList] = useState([]);
  const [showExamTypeError, setShowExamTypeError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState("");
  const saveAttemptRef = useRef(0);
  const savingRef = useRef(false);
  const activeRef = useRef(false);

  useEffect(() => {
    activeRef.current = open;
    savingRef.current = false;
    setIsSubmitting(false);
    setSaveError("");
    return () => {
      activeRef.current = false;
      savingRef.current = false;
      saveAttemptRef.current += 1;
    };
  }, [open]);

  const dispatch = useDispatch();

  const categoriesData = useSelector((state) => state.categoriesList);

  useEffect(() => {
    if (open) {
      if (!categoriesData?.categoriesList || categoriesData?.categoriesList?.examType?.length === 0) {
        dispatch(getCategories());
      }
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (categoriesData && Object.keys(categoriesData)?.length) {
      setExamTypeList(categoriesData?.categoriesList?.examType || []);
    }
  }, [categoriesData]);

  useEffect(() => {
    if (!open || seedAppliedRef.current || !seedExamType || examTypeList.length === 0) {
      return;
    }
    seedAppliedRef.current = true;
    const seeded = resolveSeedExamType(seedExamType, examTypeList);
    if (seeded) {
      setSelectedExamType((current) => (current.length ? current : [seeded]));
      setSeededExamType(seeded);
    }
  }, [open, seedExamType, examTypeList]);

  // Without this, if the getCategories request finishes with no exam types
  // (network error -> categoriesList: null, or any response missing examType),
  // the mandatory modal shows "Loading exam types..." forever with no way out
  // (it can't be dismissed with nothing selected) — a dead end. Treat "request
  // finished (loading === false) but no exam types" as a failed load so we can
  // offer a retry. (loading is undefined before the first request, so this
  // never fires prematurely; read examType from the store to avoid a flash
  // while the local examTypeList state catches up.)
  const loadFailed =
    categoriesData?.loading === false &&
    !(categoriesData?.categoriesList?.examType?.length);

  // Opened text is brought on screen (it sits at the end of the scrolling
  // middle); focus stays on the control that opened it.
  useEffect(() => {
    if (termsOpen) termsRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [termsOpen]);

  useEffect(() => {
    if (!termsFocusRequest || !termsRef.current) return;
    termsRef.current.scrollTop = 0;
    termsRef.current.focus();
    termsRef.current.scrollIntoView?.({ block: "nearest" });
  }, [termsFocusRequest]);

  const showAgreementText = (evt) => {
    evt.preventDefault();
    setTermsOpen(true);
    setTermsFocusRequest((n) => n + 1);
  };
  // A new tab keeps this step and the unticked box where they are; the native
  // app has no tabs, so it opens the in-app browser, as the plans page does.
  const openPrivacyPolicy = async (evt) => {
    if (!isNativeCapacitorRuntime()) {
      return;
    }
    evt.preventDefault();
    try {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url: AGREEMENT_PRIVACY_POLICY_URL });
    } catch {
      window.open(AGREEMENT_PRIVACY_POLICY_URL, "_blank", "noopener");
    }
  };

  const handleSubmit = () => {
    if (savingRef.current || !open) return;
    if (withAgreement && !agreed) return;
    if (selectedExamType.length === 0) {
      setShowExamTypeError(true);
      return;
    }
    const userId = window.localStorage.getItem("userId");
    if (!userId) {
      setSaveError("Your session has changed. Please sign in again.");
      return;
    }
    const attempt = ++saveAttemptRef.current;
    savingRef.current = true;
    setIsSubmitting(true);
    setShowExamTypeError(false);
    setSaveError("");
    const stillCurrent = () => {
      if (!activeRef.current || attempt !== saveAttemptRef.current) return false;
      if (window.localStorage.getItem("userId") !== userId) {
        savingRef.current = false;
        setIsSubmitting(false);
        setSaveError("Your session has changed. Please sign in again.");
        return false;
      }
      return true;
    };
    const failed = () => {
      savingRef.current = false;
      setIsSubmitting(false);
      setSaveError("We couldn't save your exam preferences. Your selection is still here. Please try again.");
    };
    if (typeof onSaveStart === "function") onSaveStart();
    // Save the exam choice before acknowledging onboarding. Each callback
    // belongs to this attempt; old Redux results cannot complete a later save.
    dispatch({
      ...setCategoryPreferences({
        userId,
        categoryMap: { discipline: [], organSystem: [], examType: [...selectedExamType] }
      }),
      meta: { onSettled: (categoriesSaved) => {
        if (!stillCurrent()) return;
        if (!categoriesSaved) { failed(); return; }
        dispatch({
          ...setPreference({
            userId,
            body: { userAcknowledged: true, quizPreference: { saveCategoriesPreference: true } }
          }),
          meta: { onSettled: (preferencesSaved) => {
            if (!stillCurrent()) return;
            if (!preferencesSaved) { failed(); return; }
            savingRef.current = false;
            setIsSubmitting(false);
            onComplete();
          } }
        });
      } }
    });
  };

  const showSeedNote = !!seededExamType && selectedExamType.length === 1 && selectedExamType[0] === seededExamType;
  // Why Continue is not available yet, said next to it (and announced): the
  // button stays disabled until an exam is chosen and the box is ticked.
  const continueHint = withAgreement && !isSubmitting && !saveError
    ? (!selectedExamType.length && !agreed ? "Choose your exam and tick the box to continue."
      : !agreed ? "Tick the box to agree and continue."
        : !selectedExamType.length ? "Choose your exam to continue." : "")
    : "";
  // The combined step lists the exams flat (no collapsible section, no
  // select-all), in the order the picker uses.
  const sortedExamTypes = [...examTypeList].sort((a, b) => a.localeCompare(b));
  const toggleExam = (exam, checked) => handleExamTypeSelect(checked
    ? [...selectedExamType, exam]
    : selectedExamType.filter((item) => item !== exam));

  const handleExamTypeSelect = (quesArr) => {
    if (savingRef.current) return;
    setSelectedExamType([...quesArr]);
    if (quesArr.length > 0) {
      setShowExamTypeError(false);
    }
  };

  return (
    <>
      <Modal
        className={withAgreement ? "exam-preferences-modal with-agreement" : "exam-preferences-modal"}
        open={open}
        onClose={(_, reason) => {
          // The new-account step cannot be dismissed, and says what is missing itself.
          if (withAgreement) return;
          if (mandatory && selectedExamType.length === 0) {
            setShowExamTypeError(true);
            return;
          }
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Fade in={open}>
          <Box
            className="modal-container"
            {...(withAgreement ? {
              role: "dialog",
              "aria-modal": "true",
              "aria-labelledby": "onboarding-step-title",
              "aria-describedby": showSeedNote ? "onboarding-step-subtitle onboarding-seed-note" : "onboarding-step-subtitle",
            } : {})}
          >
            {withAgreement ? (
              <>
                <Typography variant="h5" component="h2" id="onboarding-step-title" className="header">
                  Welcome to QLearn
                </Typography>
                <Typography variant="body2" id="onboarding-step-subtitle" className="subheader">
                  Choose your exam, then agree to the terms to start practicing.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="h5" className="header">
                  Select Your Exam Preferences
                </Typography>
                <Typography variant="body2" className="subheader">
                  Please select at least one exam type to personalize your learning experience
                </Typography>
              </>
            )}
            {showSeedNote && (
              <Typography variant="body2" id="onboarding-seed-note" className="seeded-exam-note">
                {seedNote(seedExamType, seedSource)}
              </Typography>
            )}
            {mandatory && !withAgreement && (
              <Typography variant="caption" style={{ color: 'var(--error-text, #c62828)', marginTop: '8px', display: 'block', textAlign: 'center' }}>
                * This step is mandatory and cannot be skipped
              </Typography>
            )}

            <Box className="content-box">
              {examTypeList.length > 0 && withAgreement ? (
                <fieldset className="exam-choice" disabled={isSubmitting}>
                  <legend className="exam-choice-legend">Your exam</legend>
                  {sortedExamTypes.map((exam) => (
                    <FormControlLabel
                      key={exam}
                      className="exam-choice-option"
                      control={
                        <Checkbox
                          color="primary"
                          name={exam}
                          checked={selectedExamType.includes(exam)}
                          disabled={isSubmitting}
                          onChange={(evt) => toggleExam(exam, evt.target.checked)}
                        />
                      }
                      label={exam}
                    />
                  ))}
                </fieldset>
              ) : examTypeList.length > 0 ? (
                <Categories
                  onExamTypeCatSelect={handleExamTypeSelect}
                  selectedExamType={selectedExamType}
                  examType={examTypeList}
                  isViewDisabled={isSubmitting}
                  hideCategoryWhenEmpty={true}
                  hidePowerSearch={true}
                  defaultExpanded="examType"
                />
              ) : loadFailed ? (
                <Box className="loading-container">
                  <Typography color="error" style={{ marginBottom: '12px', color: 'var(--error-text, #c62828)' }}>
                    Couldn't load exam types. Please check your connection and try again.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => dispatch(getCategories())}
                  >
                    Retry
                  </Button>
                </Box>
              ) : (
                <Box
                  className="loading-container"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "14px",
                  }}
                >
                  <CircularProgress size={28} />
                  <Typography>Loading exam types...</Typography>
                </Box>
              )}

              {showExamTypeError && (
                <Typography
                  variant="body2"
                  color="error"
                  className="error-message"
                  style={{ fontWeight: 'bold', marginTop: '10px' }}
                >
                  ⚠️ Please select at least one exam type to continue
                </Typography>
              )}

              {withAgreement && (
                <Box className="agreement-terms">
                  <Button
                    className="agreement-terms-toggle"
                    aria-expanded={termsOpen}
                    aria-controls="user_agreement"
                    onClick={() => setTermsOpen((isOpen) => !isOpen)}
                    endIcon={<ExpandMore className={termsOpen ? "is-open" : undefined} />}
                  >
                    {termsOpen ? "Hide the Terms of Use" : "Read the Terms of Use"}
                  </Button>
                  {/* Always in the page (the consent line links here); shown on request. */}
                  <Box
                    id="user_agreement"
                    ref={termsRef}
                    className="agreement-text user-agreement"
                    role="region"
                    aria-label="User Agreement and Terms of Use"
                    tabIndex={0}
                    hidden={!termsOpen}
                  >
                    {agreementText}
                  </Box>
                </Box>
              )}
            </Box>

            {saveError && <Typography role="alert" color="error">{saveError}</Typography>}
            <Box className="action-buttons">
              {withAgreement && (
                <FormControlLabel
                  className="agreement-consent"
                  control={
                    <Checkbox
                      onChange={(evt) => setAgreed(evt.target.checked)}
                      checked={agreed}
                      disabled={isSubmitting}
                      name="agreement"
                      color="primary"
                    />
                  }
                  label={
                    <>
                      I agree to the{' '}
                      <a href="#user_agreement" className="agreement-legal-link" onClick={showAgreementText}>Terms of Use</a>
                      {' '}above and the{' '}
                      <a
                        href={AGREEMENT_PRIVACY_POLICY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="agreement-legal-link"
                        onClick={openPrivacyPolicy}
                      >
                        Medlearnity Privacy Policy
                      </a>
                    </>
                  }
                />
              )}
              {withAgreement && (
                <Typography id="onboarding-continue-hint" className="continue-hint" variant="body2" aria-live="polite">
                  {continueHint}
                </Typography>
              )}
              {/* Continue needs the tick; there is no scroll-to-bottom gate. */}
              <Button
                variant="contained"
                color={selectedExamType.length > 0 ? "primary" : "default"}
                onClick={handleSubmit}
                disabled={isSubmitting || examTypeList.length === 0
                  || (withAgreement && (!agreed || selectedExamType.length === 0))}
                aria-describedby={continueHint ? "onboarding-continue-hint" : undefined}
                className="submit-btn"
                style={withAgreement ? undefined : {
                  opacity: selectedExamType.length === 0 ? 0.6 : 1,
                  cursor: selectedExamType.length === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmitting ? "Saving..." : saveError ? "Try again"
                  : (selectedExamType.length === 0 && !withAgreement) ? "Select Exam Type to Continue" : "Continue"}
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

    </>
  );
}
